import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  MountingMode,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@grnhse/seedling/lib/birch/components/tabs/';
import ApplicationTabPanel from 'talent_matching/components/side_panel/application_tab_panel';
import ApplicationHistoryTabPanel from 'talent_matching/components/side_panel/application_history_tab_panel';
import FeedbackTabPanel from 'talent_matching/components/side_panel/feedback_tab_panel';
import ResumeTabPanel from 'talent_matching/components/side_panel/resume_tab_panel';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { withLocalePrefix } from 'shared/utils/translation';
import { Document, Person } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import {
  hasApplicationHistory,
  hasApplicationQuestions,
  hasFeedback,
  hasResume,
} from 'talent_matching/components/side_panel/side_panel_tab_helpers';

const t = withLocalePrefix('talent_matching');

const TabsContainer = styled(Tabs)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-top: ${Tokens.spacing['1']};
`;

const TabListContainer = styled(Stack)`
  && div > div > div {
    column-gap: ${Tokens.spacing['1']};
  }
`;

type Props = {
  application: Person | null;
  activeDocument: Document | null;
  onOpenDocument: (document: Document) => void;
  openFeedbackModal: () => void;
};

const SidePanelTabs = ({
  application,
  activeDocument,
  onOpenDocument,
  openFeedbackModal,
}: Props) => {
  const { activeSidePanelTab, setActiveSidePanelTab } = useUserContext();

  const hasApplicationTab = hasApplicationQuestions(application);
  const hasApplicationHistoryTab = hasApplicationHistory(application);
  const hasFeedbackTab = hasFeedback(application);

  const tabsConfig = [
    {
      id: 'application',
      label: t('panel.application.title'),
      isVisible: hasApplicationTab,
      panel: <ApplicationTabPanel candidate={application} />,
    },
    {
      id: 'resume',
      label: t('panel.resume.title'),
      isVisible: hasResume(application),
      panel: (
        <ResumeTabPanel
          candidate={application}
          activeDocument={activeDocument}
          onOpenDocument={onOpenDocument}
        />
      ),
    },
    {
      id: 'application_history',
      label: t('panel.application_history.title'),
      isVisible: hasApplicationHistoryTab,
      panel: (
        <ApplicationHistoryTabPanel
          historyUrl={application?.applicationHistoryPath}
        />
      ),
    },
    {
      id: 'feedback',
      label: t('panel.feedback.title'),
      isVisible: hasFeedbackTab,
      panel: (
        <FeedbackTabPanel
          candidate={application}
          openFeedbackModal={openFeedbackModal}
        />
      ),
    },
  ];

  const visibleTabs = tabsConfig.filter((tab) => tab.isVisible);
  const visibleTabIds = visibleTabs.map((tab) => tab.id);
  const selectedTabIndex = Math.max(
    visibleTabIds.indexOf(activeSidePanelTab ?? 'application'),
    0
  );

  const handleTabChange = (index: number) => {
    const tabId = visibleTabIds[index];
    if (tabId) {
      setActiveSidePanelTab(tabId);
    }
  };

  const tabIndex = (tabId: string) => visibleTabIds.indexOf(tabId);

  return (
    <TabsContainer index={selectedTabIndex} onChange={handleTabChange}>
      <TabListContainer>
        <TabList>
          {visibleTabs.map((tab) => (
            <Tab key={tab.id} index={tabIndex(tab.id)}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
      </TabListContainer>
      <TabPanels mountingMode={MountingMode.All}>
        {visibleTabs.map((tab) => (
          <TabPanel key={tab.id} index={tabIndex(tab.id)}>
            {tab.panel}
          </TabPanel>
        ))}
      </TabPanels>
    </TabsContainer>
  );
};

export default SidePanelTabs;
